import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Download, Loader2, ScanSearch } from "lucide-react";

import {
  useRunWorkAnalysis,
  useWorkAnalysis,
} from "@/hooks/queries/useWorkAnalysis";
import {
  useBidDocumentState,
  usePrepareBidDocuments,
  useUploadBidDocument,
} from "@/hooks/queries/useWorkAdvisoryBases";
import { VERDICT_LABELS } from "@/lib/workadvisory-score";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type {
  AnalysisRow,
  AnalysisRowStatus,
  BidDocumentState,
  FrozenAnalysis,
} from "@/types/workadvisory";

const STATUS: Record<AnalysisRowStatus, { label: string; tone: string }> = {
  CUMPLE: {
    label: "Cumple",
    tone: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
  CUMPLE_CON_OBSERVACION: {
    label: "Cumple con obs.",
    tone: "bg-emerald-50/70 text-emerald-700 dark:bg-emerald-950/25 dark:text-emerald-300",
  },
  SE_COMPROMETE: {
    label: "Se compromete",
    tone: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
  },
  REVISAR: {
    label: "Revisar",
    tone: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  },
  NO_CUMPLE: {
    label: "No cumple",
    tone: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  },
  NO_APLICA: { label: "No aplica", tone: "bg-muted text-muted-foreground" },
};

const CATEGORY_LABELS: Record<string, string> = {
  DURO: "Requisitos duros",
  PUNTAJE: "Factores de puntaje",
  CONTRACTUAL: "Condiciones del contrato",
  INFORMATIVO: "Trámite y formatos",
};

const EFFORT: Record<string, { label: string; tone: string }> = {
  ACREDITADO: { label: "ya lo tiene", tone: "text-emerald-600" },
  ALCANZABLE: { label: "a su alcance", tone: "text-amber-600" },
  REQUIERE_INVERSION: { label: "requiere inversión", tone: "text-red-600" },
};

/** Cuántos puntos acredita, cuántos están a su alcance y dónde queda la valla. */
function PointsBar({
  acreditado,
  alcanzable,
  disponible,
  valla,
}: {
  acreditado: number;
  alcanzable: number;
  disponible: number;
  valla: number | null;
}) {
  const pct = (value: number) =>
    disponible > 0 ? (value / disponible) * 100 : 0;

  return (
    <div className="min-w-52 flex-1">
      <div className="relative h-3 overflow-hidden rounded-full bg-muted">
        <div
          className="absolute inset-y-0 left-0 bg-emerald-500"
          style={{ width: `${pct(acreditado)}%` }}
        />
        <div
          className="absolute inset-y-0 bg-amber-400"
          style={{ left: `${pct(acreditado)}%`, width: `${pct(alcanzable)}%` }}
        />
        {valla != null && (
          // La valla es lo que decide si pueden competir: se marca sobre la barra.
          <div
            className="absolute inset-y-0 w-0.5 bg-foreground"
            style={{ left: `${pct(valla)}%` }}
            title={`Valla técnica: ${valla}`}
          />
        )}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        <span className="font-bold text-emerald-600">{acreditado}</span>{" "}
        acreditados
        {alcanzable > 0 && (
          <>
            {" · "}
            <span className="font-bold text-amber-600">+{alcanzable}</span> a su
            alcance
          </>
        )}
        {" de "}
        {disponible}
        {valla != null && ` · valla ${valla}`}
      </p>
    </div>
  );
}

function RowsTable({ rows }: { rows: AnalysisRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="pb-1.5 pr-3 font-semibold">Requisito</th>
            <th className="pb-1.5 pr-3 font-semibold">Piden las bases</th>
            <th className="pb-1.5 pr-3 font-semibold">Tiene la empresa</th>
            <th className="pb-1.5 pr-3 font-semibold">Estado</th>
            <th className="pb-1.5 text-right font-semibold">Fuente</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-t align-top">
              <td className="py-2.5 pr-3">
                <div className="font-semibold">{row.requisito}</div>
                {row.puntos && (
                  <div className={cn("text-[10px]", EFFORT[row.puntos.esfuerzo]?.tone)}>
                    {row.puntos.acreditados}/{row.puntos.delFactor} pts ·{" "}
                    {EFFORT[row.puntos.esfuerzo]?.label}
                  </div>
                )}
              </td>
              <td className="max-w-72 py-2.5 pr-3 text-muted-foreground">
                {row.piden}
              </td>
              <td className="max-w-72 py-2.5 pr-3">
                {row.tiene}
                {row.comparacion && (
                  <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                    {row.comparacion.tiene.toLocaleString("es-PE")}{" "}
                    {row.comparacion.criterio === "MAXIMO" ? "≤" : "≥"}{" "}
                    {row.comparacion.piden.toLocaleString("es-PE")}{" "}
                    {row.comparacion.unidad}
                  </div>
                )}
                {row.porque && (
                  <div className="mt-0.5 text-[11px] italic text-muted-foreground">
                    {row.porque}
                  </div>
                )}
              </td>
              <td className="py-2.5 pr-3">
                <span
                  className={cn(
                    "whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase",
                    STATUS[row.estado]?.tone,
                  )}
                >
                  {STATUS[row.estado]?.label ?? row.estado}
                </span>
              </td>
              <td className="py-2.5 text-right text-[11px] text-muted-foreground">
                {row.pagina ? `pág. ${row.pagina}` : "—"}
                {row.fuente && (
                  <div className="max-w-40 truncate" title={row.fuente}>
                    {row.fuente}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BASES_TONE: Record<BidDocumentState["status"], string> = {
  SIN_BASES: "bg-muted text-muted-foreground",
  EN_PROCESO: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  PENDIENTE: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  LEIDAS: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  FALLO: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
};

/** En qué punto están las bases, para saber de un vistazo qué obra está lista. */
function BasesChip({ state }: { state: BidDocumentState }) {
  const label =
    state.status === "LEIDAS"
      ? `${state.requirements} requisitos`
      : state.status === "EN_PROCESO"
        ? "leyendo bases…"
        : state.status === "SIN_BASES"
          ? "sin bases"
          : state.status === "FALLO"
            ? "falló la lectura"
            : "bases sin leer";

  return (
    <span
      title={state.detail}
      className={cn(
        "flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide",
        BASES_TONE[state.status],
      )}
    >
      {state.status === "EN_PROCESO" && (
        <Loader2 className="size-3 animate-spin" />
      )}
      {label}
    </span>
  );
}

/**
 * El cruce criterio a criterio, y el paso previo: bajar las bases y leerlas. Se
 * corre a pedido porque cada análisis es una llamada a la IA, y en un informe de
 * 10 obras no tiene sentido dispararlas todas por si acaso.
 */
export function WorkAnalysisPanel({
  contractingId,
  organizationId,
  preparing,
  onAnalyzed,
}: {
  contractingId: number;
  organizationId: string;
  /** El lote del informe está corriendo: hay que seguir el estado en vivo. */
  preparing?: boolean;
  /** Congela el cruce recién corrido dentro del borrador. */
  onAnalyzed?: (frozen: FrozenAnalysis) => void;
}) {
  const [open, setOpen] = useState(false);
  const { data: analysis, isLoading } = useWorkAnalysis(
    contractingId,
    organizationId,
  );
  const { data: bases } = useBidDocumentState(contractingId, preparing);
  const prepare = usePrepareBidDocuments();
  const upload = useUploadBidDocument();
  const run = useRunWorkAnalysis();

  const runAnalysis = () =>
    run.mutate(
      { contractingId, organizationId },
      {
        onSuccess: (res) => {
          setOpen(true);
          onAnalyzed?.(res.frozen);
          toast.success("Análisis actualizado");
        },
        onError: (err: any) => toast.error(err?.error ?? "No se pudo analizar"),
      },
    );

  // Solo se encadena el análisis cuando fue ESTA pantalla la que mandó a leer
  // las bases. Si no, abrir la revisión dispararía una llamada a la IA por cada
  // obra que ya tuviera bases leídas.
  const waitingForBases = useRef(false);

  useEffect(() => {
    if (!waitingForBases.current || bases?.status !== "LEIDAS") return;
    waitingForBases.current = false;
    runAnalysis();
  }, [bases?.status]);

  /**
   * Manda a leer las bases y encadena el cruce al terminar. Con `force` relee
   * unas ya leídas: es lo que hace "Rehacer análisis", porque rehacer el cruce
   * sobre una extracción mala devuelve el mismo veredicto malo.
   */
  const readBases = (force: boolean) =>
    prepare.mutate(
      { contractingId, force },
      {
        onSuccess: () => {
          waitingForBases.current = true;
          toast.info(
            force
              ? "Releyendo las bases. Puede tomar varios minutos."
              : "Bajando las bases. Puede tomar varios minutos.",
          );
        },
        onError: (err: any) =>
          toast.error(err?.error ?? "No se pudieron bajar las bases"),
      },
    );

  const busy =
    prepare.isPending || preparing || bases?.status === "EN_PROCESO";

  const grouped = useMemo(() => {
    const rows = analysis?.workadvisoryanalysis_rows ?? [];
    const order = ["DURO", "PUNTAJE", "CONTRACTUAL", "INFORMATIVO"];
    return order
      .map((categoria) => ({
        categoria,
        rows: rows.filter((row) => row.categoria === categoria),
      }))
      .filter((group) => group.rows.length > 0);
  }, [analysis]);

  const points = analysis?.workadvisoryanalysis_points;
  const verdict = analysis?.workadvisoryanalysis_verdict;

  return (
    <div className="mt-3 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          <ScanSearch className="size-3.5" />
          Criterio a criterio
          <ChevronDown
            className={cn("size-3.5 transition-transform", open && "rotate-180")}
          />
        </button>

        <div className="flex flex-1 flex-wrap items-center justify-end gap-3">
          {bases && <BasesChip state={bases} />}
          {verdict && (
            <span
              className={cn(
                "rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide",
                VERDICT_LABELS[verdict].tone,
              )}
            >
              {VERDICT_LABELS[verdict].label}
            </span>
          )}
          {points && (
            <PointsBar
              acreditado={points.acreditado}
              alcanzable={points.alcanzable}
              disponible={points.disponible}
              valla={points.valla}
            />
          )}
          {/* Rehacer arrastra la relectura de las bases: si el cruce salió mal,
              la causa suele estar en la extracción, no en el cruce. */}
          {bases?.status === "LEIDAS" && !analysis ? (
            <Button
              size="sm"
              variant="outline"
              disabled={run.isPending}
              onClick={runAnalysis}
            >
              {run.isPending && (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              )}
              Analizar
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={busy || run.isPending}
              onClick={() => readBases(bases?.status === "LEIDAS")}
            >
              {busy || run.isPending ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <Download className="mr-1.5 size-3.5" />
              )}
              {busy
                ? "Leyendo bases…"
                : bases?.status === "LEIDAS"
                  ? "Rehacer análisis"
                  : "Bajar y leer bases"}
            </Button>
          )}
        </div>
      </div>

      {/* Unas bases que no entran en el request no se arreglan solas: el sistema
          dice cuánto pesan y acá se sube la versión optimizada. Solo aparece
          cuando la lectura falló, que es el único caso en que sirve. */}
      {bases?.status === "FALLO" && (
        <div className="mt-2 rounded-lg border border-dashed p-2.5">
          <p className="text-[11px] leading-snug text-muted-foreground">
            {bases.detail} Sube un PDF optimizado —baja la resolución o pásalo a
            escala de grises, no aplastes la calidad— y se vuelve a leer solo.
          </p>
          <input
            type="file"
            accept="application/pdf"
            disabled={upload.isPending || busy}
            className="mt-2 block w-full text-[11px] file:mr-2 file:rounded-md file:border file:bg-muted file:px-2 file:py-1 file:text-[11px] file:font-semibold"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              upload.mutate(
                { contractingId, file },
                {
                  onSuccess: () => {
                    toast.success("Bases reemplazadas. Leyéndolas…");
                    readBases(false);
                  },
                  onError: (err: any) =>
                    toast.error(err?.error ?? "No se pudo subir el archivo"),
                },
              );
            }}
          />
          {upload.isPending && (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Loader2 className="size-3 animate-spin" /> Subiendo…
            </p>
          )}
        </div>
      )}

      {points?.incompleto && (
        <p className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          Los factores extraídos suman {points.disponible}
          {points.declaredTotal != null &&
            ` y el pliego declara ${points.declaredTotal}`}
          {points.valla != null && `, con valla ${points.valla}`}: falta cuadro de
          evaluación por leer. No se puede decir si alcanzan la valla — vuelve a
          leer las bases antes de fiarte del puntaje.
        </p>
      )}

      {points?.alcanzaLaValla === false && (
        <p className="mt-2 rounded-md bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800 dark:bg-red-950/40 dark:text-red-300">
          Ni acreditando todo lo que está a su alcance llegan a la valla de{" "}
          {points.valla}: quedarían en {points.acreditado + points.alcanzable}.
        </p>
      )}

      {open && (
        <div className="mt-3 space-y-4">
          {isLoading && <Loader2 className="size-4 animate-spin" />}

          {!isLoading && !analysis && (
            <p className="text-xs text-muted-foreground">
              Todavía no se ha cruzado esta obra contra la ficha de la empresa.
              {bases ? ` ${bases.detail}` : ""}
            </p>
          )}

          {points?.resumen && (
            <p className="rounded-md bg-muted/50 px-3 py-2 text-xs leading-relaxed">
              {points.resumen}
            </p>
          )}

          {grouped.map((group) => (
            <div key={group.categoria}>
              <h4 className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                {CATEGORY_LABELS[group.categoria] ?? group.categoria}
              </h4>
              <RowsTable rows={group.rows} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
